import { isTextBoxActive, isTextSelected } from '@prophecy/utils/dom';
import { findNextEmptyCell } from '@prophecy/utils/graph';
import { KeyCodes } from '@prophecy/utils/keyboard';
import { useAutoFocus } from '@prophecy/utils/react/focus'; // eslint-disable-line import/no-webpack-loader-syntax
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { easeCubic } from 'd3-ease';
import { zoomIdentity } from 'd3-zoom';
import { isEqual, noop } from 'lodash-es';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { getConnectedEdges, getRectOfNodes, getTransformForBounds, useEdges, useNodes, useReactFlow, useStore as useReactFlowStore, useStoreApi } from 'reactflow';
import { isDialogOpened } from '../Dialog';
import { isCommentNodeEditorActive } from '../MarkdownEditor/utils';
import { LS_SUBFLOWS_DEBUG_MODE_KEY } from './debugConstants';
import { useGraphContext } from './GraphContext';
import { NODE_DIMENSION } from './tokens';
import { defaultFilterNodes, isGraphPresent, stripNodeProperties, isNodeNotPositioned, getNodeRect, snapPosition } from './utils';
export const MouseSelectionKey = KeyCodes._None;
export const IDE_Clipboard_Data_Format = 'application/prophecy-ide-format';
function getSelectedGraph(_nodes, _edges) {
    const selectedNodeIds = [];
    const inBoundEdges = [];
    const edges = _edges;
    const nodes = [];
    _nodes.forEach((node)=>{
        selectedNodeIds.push(node.id);
        nodes.push(stripNodeProperties(node));
    });
    const connectedEdges = getConnectedEdges(nodes, edges);
    connectedEdges.forEach((edge)=>{
        if (selectedNodeIds.includes(edge.source) && selectedNodeIds.includes(edge.target)) {
            inBoundEdges.push(edge);
        }
    });
    return {
        nodes,
        edges: inBoundEdges
    };
}
function defaultCopy(a) {
    return a;
}
/**
 * Hook that returns the currently selected nodes and edges in a
 * React Flow graph.
 *
 * Note: useNodes returns new references for nodes even before dimensions are
 * calculated, so ensure to use this hook only after dimensions are calculated
 * as it may lead to unnecessary re-renders
 *
 * @returns An object containing:
 *   - selectedNodes: Array of currently selected nodes in the graph
 *   - selectedEdges: Array of currently selected edges in the graph
 */ export function useSelectedElements() {
    const nodes = useNodes();
    const edges = useEdges();
    const selectedNodes = nodes.filter((node)=>node.selected);
    const selectedEdges = edges.filter((edge)=>edge.selected);
    return {
        selectedNodes,
        selectedEdges
    };
}
/**
 * Hook that returns the currently selected process IDs in a
 * React Flow graph.
 *
 * @returns An array of currently selected process IDs
 */ export const useSelectedProcessIds = ()=>{
    const selectedNodeIds = useReactFlowStore((state)=>Array.from(state.nodeInternals.entries()).filter(([, node])=>node.selected).map(([, node])=>node.id), (prev, next)=>{
        return isEqual(prev, next);
    });
    return selectedNodeIds;
};
function isCutCopyAllowed() {
    // if user has selected some other text, or user is inside input/textarea or dialog, don't allow cut and copy
    return !(isTextSelected() || isTextBoxActive() || isDialogOpened() || isCommentNodeEditorActive()) && isGraphPresent();
}
export function useCopyPaste(onCut = defaultCopy, onCopy = defaultCopy, onPaste = noop, mousePositionRef, readOnly) {
    const { selectedNodes, selectedEdges } = useSelectedElements();
    const _onCut = usePersistentCallback(onCut);
    const _onCopy = usePersistentCallback(onCopy);
    const _onPaste = usePersistentCallback(onPaste);
    useEffect(()=>{
        async function cut(event) {
            if (!isCutCopyAllowed()) {
                return;
            }
            if (selectedNodes && selectedNodes.length) {
                const { nodes, edges } = getSelectedGraph(selectedNodes, selectedEdges);
                const data = await _onCut({
                    nodes,
                    edges
                });
                if (!data) return;
                event.clipboardData.setData(IDE_Clipboard_Data_Format, JSON.stringify(data));
                event.preventDefault();
            }
        }
        async function copy(event) {
            if (!isCutCopyAllowed()) {
                return;
            }
            // const selectedElements = store.selectedElements;
            if (selectedNodes && selectedNodes.length) {
                const { nodes, edges } = getSelectedGraph(selectedNodes, selectedEdges);
                const data = await _onCopy({
                    nodes,
                    edges
                });
                if (!data) return;
                event.clipboardData.setData(IDE_Clipboard_Data_Format, JSON.stringify(data));
                event.preventDefault();
            }
        }
        function paste(event) {
            if (isDialogOpened() || !isGraphPresent() || isTextBoxActive() || isCommentNodeEditorActive()) {
                return;
            }
            try {
                const dataStr = event.clipboardData.getData(IDE_Clipboard_Data_Format);
                const data = JSON.parse(dataStr);
                _onPaste(data, mousePositionRef.current);
            } catch (error) {
                console.error(error);
            } finally{
                event.preventDefault();
            }
        }
        if (!readOnly) {
            document.addEventListener('cut', cut);
            document.addEventListener('paste', paste);
        }
        document.addEventListener('copy', copy);
        return ()=>{
            if (!readOnly) {
                document.removeEventListener('cut', cut);
                document.removeEventListener('paste', paste);
            }
            document.removeEventListener('copy', copy);
        };
    }, [
        _onCopy,
        _onCut,
        _onPaste,
        mousePositionRef,
        readOnly,
        selectedEdges,
        selectedNodes
    ]);
}
export function withPadding(boundary, padding) {
    return [
        [
            boundary[0][0] - padding,
            boundary[0][1] - padding
        ],
        [
            boundary[1][0] + padding,
            boundary[1][1] + padding
        ]
    ];
}
function isNodePositioned(node) {
    return isFinite(node.position?.x) && isFinite(node.position?.y);
}
function isValidViewport(vp) {
    return isFinite(vp.x) && isFinite(vp.y) && isFinite(vp.zoom) && vp.zoom > 0;
}
export function useBoundary(nodes, graphKey, padding = 200) {
    const { width, height, zoom } = useReactFlowStore((s)=>({
            width: s.width,
            height: s.height,
            zoom: s.transform[2]
        }), shallowEqual);
    // get nodes bound
    const [initialized, initialize] = useState();
    useEffect(()=>{
        initialize(false);
    }, [
        graphKey
    ]);
    const [, setInitializeCounter] = useState(0);
    // Last padded boundary when layout was valid and fallback when nodes are unlayouted
    const previousBoundary = useRef();
    /**
   * Max node bounding rects (flow coords without viewport padding).
   * Viewport padding is recomputed on each frame from current zoom.
   */ const bounds = useMemo(()=>{
        const nodesWithWidthAndHeight = nodes.filter(isNodePositioned).map((node)=>({
                ...node,
                // consider width and height from data if not present from node as subflow nodes can be bigger than default dimensions
                width: node.width || node.data.width || NODE_DIMENSION,
                height: node.height || node.data.height || NODE_DIMENSION
            }));
        return getRectOfNodes(nodesWithWidthAndHeight);
    }, [
        nodes
    ]);
    const initializeBoundary = usePersistentCallback(()=>{
        // on initialize clear previous boundary
        previousBoundary.current = undefined;
        // on every initialize make sure the boundary is recalculated, we do it by triggering a rerender based on counter
        setInitializeCounter((prev)=>prev + 1);
        initialize(true);
    });
    const hasUnLayoutedNodes = nodes.some(isNodeNotPositioned);
    let boundary;
    // return boundary based on viewport size, if positions are not initialized or nodes are not present
    if (!nodes.length || !initialized || !isFinite(bounds.width) || !isFinite(bounds.height)) {
        boundary = [
            [
                0,
                0
            ],
            [
                width,
                height
            ]
        ];
    // if it is initialized but unlayouted node, default to previous boundary
    } else if (hasUnLayoutedNodes) {
        boundary = previousBoundary.current || [
            [
                0,
                0
            ],
            [
                width,
                height
            ]
        ];
    } else {
        const nodeBoundingRect = [
            [
                bounds.x,
                bounds.y
            ],
            [
                bounds.x + bounds.width,
                bounds.y + bounds.height
            ]
        ];
        const nodeBoundingRectWidth = nodeBoundingRect[1][0] - nodeBoundingRect[0][0];
        const nodeBoundingRectHeight = nodeBoundingRect[1][1] - nodeBoundingRect[0][1];
        const flowViewportWidth = width / zoom;
        const flowViewportHeight = height / zoom;
        const extraPaddingX = Math.max(flowViewportWidth - nodeBoundingRectWidth, 0) / 2;
        const extraPaddingY = Math.max(flowViewportHeight - nodeBoundingRectHeight, 0) / 2;
        /**
     * Compute the boundary based on the max node bounding rect and the extra padding
     */ const boundMinX = nodeBoundingRect[0][0] - extraPaddingX;
        const boundMinY = nodeBoundingRect[0][1] - extraPaddingY;
        const boundMaxX = nodeBoundingRect[1][0] + extraPaddingX;
        const boundMaxY = nodeBoundingRect[1][1] + extraPaddingY;
        boundary = [
            [
                boundMinX,
                boundMinY
            ],
            [
                boundMaxX,
                boundMaxY
            ]
        ];
        previousBoundary.current = boundary;
        const flowPadding = padding / zoom;
        boundary = withPadding(boundary, flowPadding);
        previousBoundary.current = boundary;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedBoundary = useMemo(()=>boundary, [
        JSON.stringify(boundary)
    ]);
    return {
        initializeBoundary,
        boundary: memoizedBoundary,
        boundaryInitialized: Boolean(initialized)
    };
}
const DEFAULT_MIN_ZOOM = 0.65;
/**
 * Hook to fit the nodes in view port. It will fit the nodes in view port based on the bounds of the nodes.
 * @param filter - The filter to apply to the nodes to fit in view port. If not provided, all nodes will be used.
 * @param applyZoom - Whether to apply zoom to the nodes to fit in view port. If not provided, the current zoom will be used.
 * @returns A callback function to fit the nodes in view port.
 */ export function useFitView(filter = defaultFilterNodes, applyZoom = false, minZoom = DEFAULT_MIN_ZOOM) {
    const store = useStoreApi();
    const instance = useReactFlow();
    return usePersistentCallback((nodesToFitInViewPort)=>{
        const { width, height, transform, d3Zoom, d3Selection } = store.getState();
        const maxZoom = 1 / 1.2;
        const nodes = instance.getNodes();
        // if we specify specific nodes to fit in view port, use them instead of all nodes
        const _nodes = (nodesToFitInViewPort || nodes.filter(filter)).filter(isNodePositioned);
        if (!_nodes.length || !d3Zoom || !d3Selection) {
            return;
        }
        const currentZoom = transform[2];
        const bounds = getRectOfNodes(_nodes);
        let [x, y, zoom] = getTransformForBounds(bounds, width, height, applyZoom ? minZoom : currentZoom, applyZoom ? maxZoom : currentZoom, 0.1);
        // if the bounds with new zoom doesn't fit on view port,
        if (bounds.width * zoom > width || bounds.height * zoom > height) {
            const { nodeInternals } = store.getState();
            // try to bring the left and top most in the view port.
            const nodesOnLeftEdge = _nodes.filter((node)=>getNodeRect(node, nodeInternals).x === bounds.x);
            // sort the node based on y position
            const referenceNode = nodesOnLeftEdge.sort((node1, node2)=>{
                return getNodeRect(node1, nodeInternals).y - getNodeRect(node2, nodeInternals).y;
            })[0];
            const referenceNodeRect = getNodeRect(referenceNode, nodeInternals);
            const padding = 50;
            // if horizontal bound is not fitting on viewport update x
            x = bounds.width * zoom > width ? -(referenceNodeRect.x - padding) * zoom : x;
            // if vertical bound not fitting on view port update y
            // For y try to center reference node, but if vertically centering reference node brings the bound inside the viewport,
            // reset to the bound, so more nodes can be show
            y = bounds.height * zoom > height ? -(Math.max(bounds.y * zoom, referenceNodeRect.y * zoom - height / 2) - padding * zoom) : y;
        }
        if (!isValidViewport({
            x,
            y,
            zoom
        })) {
            return;
        }
        const newTransform = zoomIdentity.translate(x, y).scale(zoom);
        d3Zoom.transform(d3Selection, newTransform);
    });
}
export function useRestoreOrFitView({ filter = defaultFilterNodes, applyZoom = false, reLayout = false, graphId }) {
    const fitView = useFitView(filter, applyZoom);
    const { viewPortCache } = useGraphContext();
    const { getViewport, setViewport, getNodes } = useReactFlow();
    const _getViewport = usePersistentCallback(getViewport);
    const _getNodes = usePersistentCallback(getNodes);
    // if graph requires re layout clear the key
    useEffect(()=>{
        if (reLayout) {
            viewPortCache.delete(graphId);
        }
    }, [
        graphId,
        reLayout,
        viewPortCache
    ]);
    useLayoutEffect(()=>{
        return ()=>{
            // store the viewport information if nodes are present
            const vp = _getViewport();
            if (_getNodes().length && isValidViewport(vp)) {
                viewPortCache.set(graphId, vp);
            }
        };
    }, [
        graphId,
        _getNodes,
        viewPortCache,
        _getViewport
    ]);
    const restoreOrFitView = usePersistentCallback(()=>{
        const viewPort = viewPortCache.get(graphId);
        const nodes = getNodes();
        if (!nodes.length) {
            fitView();
            return;
        }
        if (viewPort && isValidViewport(viewPort)) {
            setViewport(viewPort);
        } else {
            viewPortCache.delete(graphId);
            fitView();
        }
    });
    return {
        restoreOrFitView,
        fitView
    };
}
function smallestNearestMultiple(value, multipleOf) {
    return value - value % multipleOf;
}
export function useEmptyCell() {
    const store = useStoreApi();
    const { getViewport, setViewport } = useReactFlow();
    return usePersistentCallback((nodePoints, direction)=>{
        const { width: containerWidth, height: containerHeight, transform: [tX, tY, tScale] } = store.getState();
        const containerPadding = 50;
        const nodeGap = 60;
        // start the position from factor of grid size
        const { x: leftCornerX, y: leftCornerY } = snapPosition({
            x: -tX / tScale,
            y: -tY / tScale
        });
        const { cell, offset } = findNextEmptyCell({
            points: nodePoints,
            height: (containerHeight - containerPadding) / tScale,
            width: (containerWidth - containerPadding) / tScale,
            leftCornerX: smallestNearestMultiple(leftCornerX, NODE_DIMENSION + nodeGap),
            leftCornerY: smallestNearestMultiple(leftCornerY, NODE_DIMENSION + nodeGap),
            direction
        });
        if (offset) {
            // Update viewport to center on the new cell position
            const viewport = getViewport();
            setViewport({
                ...viewport,
                x: viewport.x + offset.x,
                y: viewport.y + offset.y
            });
        }
        return [
            cell.x,
            cell.y
        ];
    });
}
export function useKeyBindings({ autoFocus, initialized, container, openSearch }) {
    const { getNodes, getEdges, setEdges, setNodes } = useReactFlow();
    const nodes = getNodes();
    const edges = getEdges();
    useAutoFocus(initialized && autoFocus, container);
    const handleSelectAll = (e)=>{
        if ((e.metaKey || e.ctrlKey) && e.key === KeyCodes.A) {
            e.preventDefault();
            const _nodes = [
                ...nodes
            ].map((node)=>({
                    ...node,
                    selected: !node.data.virtual
                }));
            const _edges = [
                ...edges
            ].map((edge)=>({
                    ...edge,
                    selected: !edge.data?.virtual
                }));
            setNodes(_nodes);
            setEdges(_edges);
        }
    };
    const handleSearch = (e)=>{
        if (e.key === KeyCodes.F && (e.metaKey || e.ctrlKey) && openSearch) {
            e.preventDefault();
            openSearch();
        }
    };
    return (e)=>{
        if (!initialized || isTextBoxActive() || isCommentNodeEditorActive()) {
            return;
        }
        handleSelectAll(e);
        handleSearch(e);
    };
}
/**
 * Hook to bring nodes into viewport within defined boundaries
 */ export function useBringNodesInViewPort({ duration = 400, leftBoundary = 100, topBoundary = 100, rightBoundary = 100, bottomBoundary = 100 } = {}) {
    const store = useStoreApi();
    const bringNodesInViewport = usePersistentCallback(async (nodes)=>{
        const { width, height, transform, d3Zoom, d3Selection } = store.getState();
        if (!d3Zoom || !d3Selection || !nodes.length) {
            return;
        }
        const [currentX, currentY, currentZoom] = transform;
        // Get bounding rectangle of all nodes
        const bounds = getRectOfNodes(nodes);
        // Convert bounds to screen coordinates to check boundaries
        const boundsScreenX = bounds.x * currentZoom + currentX;
        const boundsScreenY = bounds.y * currentZoom + currentY;
        const boundsScreenWidth = bounds.width * currentZoom;
        const boundsScreenHeight = bounds.height * currentZoom;
        // Check if nodes are within boundary limits
        const boundsRightEdge = boundsScreenX + boundsScreenWidth;
        const boundsBottomEdge = boundsScreenY + boundsScreenHeight;
        const isWithinLeftBoundary = boundsScreenX >= leftBoundary;
        const isWithinTopBoundary = boundsScreenY >= topBoundary;
        const isWithinRightBoundary = boundsRightEdge <= width - rightBoundary;
        const isWithinBottomBoundary = boundsBottomEdge <= height - bottomBoundary;
        // If already in bounds, no need to animate
        if (isWithinLeftBoundary && isWithinTopBoundary && isWithinRightBoundary && isWithinBottomBoundary) {
            return;
        }
        // Calculate new x,y position to bring nodes into boundaries
        let newX = currentX;
        let newY = currentY;
        // Adjust X position if nodes extend beyond left boundary
        if (!isWithinLeftBoundary) {
            const excessLeft = leftBoundary - boundsScreenX;
            newX = currentX + excessLeft;
        } else if (!isWithinRightBoundary) {
            // move the nodes to little more left so two nodes animation can fit
            const excessRight = boundsRightEdge - (width - 3 * rightBoundary);
            newX = currentX - excessRight;
        }
        // Adjust Y position if nodes extend beyond top boundary
        if (!isWithinTopBoundary) {
            const excessTop = topBoundary - boundsScreenY;
            newY = currentY + excessTop;
        } else if (!isWithinBottomBoundary) {
            const excessBottom = boundsBottomEdge - (height - bottomBoundary);
            newY = currentY - excessBottom;
        }
        const newTransform = zoomIdentity.translate(newX, newY).scale(currentZoom);
        // Apply smooth transform with animation
        const transformPromise = new Promise((resolve, reject)=>{
            const d3Transition = d3Selection.transition().duration(duration).ease(easeCubic);
            d3Zoom.transform(d3Transition, newTransform);
            d3Transition.on('end', ()=>resolve());
            d3Transition.on('interrupt', ()=>{
                reject(new Error('Transition interrupted'));
            });
        });
        await transformPromise;
    });
    return bringNodesInViewport;
}
export function useSubflowsDebugMode() {
    const isSubflowsDebugMode = localStorage.getItem(LS_SUBFLOWS_DEBUG_MODE_KEY) === 'true';
    const log = usePersistentCallback((...args)=>{
        console.log(...args);
    });
    return {
        enabled: isSubflowsDebugMode,
        log: isSubflowsDebugMode ? log : noop
    };
}
